import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

interface Registro {
  id: string;
  nombre_apellido: string;
  dni: string;
  cuit: string;
  email: string;
  telefono_movil: string;
  fecha_registro: string;
}

export default function AdminRegistrosPage() {
  const navigate = useNavigate();
  const [registros, setRegistros] = useState<Registro[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredRegistros, setFilteredRegistros] = useState<Registro[]>([]);

  useEffect(() => {
    checkAuth();
    fetchRegistros();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredRegistros(registros);
    } else {
      const filtered = registros.filter(
        (registro) =>
          registro.nombre_apellido.toLowerCase().includes(searchTerm.toLowerCase()) ||
          registro.dni.includes(searchTerm) ||
          registro.cuit.includes(searchTerm) ||
          registro.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          registro.telefono_movil.includes(searchTerm)
      );
      setFilteredRegistros(filtered);
    }
  }, [searchTerm, registros]);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate('/admin/login');
    }
  };

  const fetchRegistros = async () => {
    try {
      const { data, error } = await supabase
        .from('registros')
        .select('*')
        .order('fecha_registro', { ascending: false });

      if (error) {
        console.error('Error al cargar registros:', error);
      } else {
        setRegistros(data || []);
        setFilteredRegistros(data || []);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#1a2b4a] to-[#d4af37] flex items-center justify-center">
              <i className="ri-admin-line text-xl text-white"></i>
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#1a2b4a]">Panel de Administración</h1>
              <p className="text-sm text-gray-500">Gestión de Registros</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="https://readdy.ai/home/b7c25f0a-e340-4824-a012-9352f5eabc8c/settings"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#1a2b4a] text-white rounded-lg hover:bg-[#2a3b5a] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-robot-line text-lg"></i>
              Configurar Asistente IA
            </a>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 hover:text-[#d4af37] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-logout-box-line text-lg"></i>
              Cerrar Sesión
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Total Registros</p>
                <p className="text-3xl font-bold text-[#1a2b4a]">{registros.length}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center">
                <i className="ri-user-line text-2xl text-blue-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Registros Hoy</p>
                <p className="text-3xl font-bold text-[#1a2b4a]">
                  {
                    registros.filter(
                      (r) =>
                        new Date(r.fecha_registro).toDateString() === new Date().toDateString()
                    ).length
                  }
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-green-50 flex items-center justify-center">
                <i className="ri-calendar-check-line text-2xl text-green-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Resultados Filtrados</p>
                <p className="text-3xl font-bold text-[#1a2b4a]">{filteredRegistros.length}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-[#d4af37]/10 flex items-center justify-center">
                <i className="ri-filter-line text-2xl text-[#d4af37]"></i>
              </div>
            </div>
          </div>

          {/* Readdy Agent Card */}
          <a
            href="https://readdy.ai/home/b7c25f0a-e340-4824-a012-9352f5eabc8c/settings"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-gradient-to-br from-[#1a2b4a] to-[#2a4a6a] rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all cursor-pointer group"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-300 mb-1">Asistente IA</p>
                <p className="text-lg font-bold text-white">Configurar</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors">
                <i className="ri-robot-line text-2xl text-white"></i>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-3">Ver conversaciones y ajustes</p>
          </a>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
              <input
                type="text"
                placeholder="Buscar por nombre, DNI, CUIT, email o teléfono..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-transparent outline-none transition-all text-sm"
              />
            </div>
            <button
              onClick={() => fetchRegistros()}
              className="px-6 py-3 bg-[#d4af37] text-white rounded-lg font-medium hover:bg-[#c19d2f] transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-refresh-line text-lg"></i>
              Actualizar
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <i className="ri-loader-4-line text-4xl text-[#d4af37] animate-spin mb-4"></i>
                <p className="text-gray-500">Cargando registros...</p>
              </div>
            </div>
          ) : filteredRegistros.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
                <p className="text-gray-500 text-lg font-medium">No se encontraron registros</p>
                <p className="text-gray-400 text-sm mt-2">
                  {searchTerm ? 'Intenta con otros términos de búsqueda' : 'Aún no hay registros en el sistema'}
                </p>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Nombre y Apellido
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      DNI
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      CUIT
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Teléfono
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Fecha de Registro
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredRegistros.map((registro) => (
                    <tr key={registro.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1a2b4a] to-[#d4af37] flex items-center justify-center text-white font-semibold text-sm">
                            {registro.nombre_apellido.charAt(0).toUpperCase()}
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {registro.nombre_apellido}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {registro.dni}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {registro.cuit}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        <a
                          href={`mailto:${registro.email}`}
                          className="text-[#d4af37] hover:underline cursor-pointer"
                        >
                          {registro.email}
                        </a>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {registro.telefono_movil}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(registro.fecha_registro)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer Info */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>
            Mostrando {filteredRegistros.length} de {registros.length} registros totales
          </p>
        </div>
      </main>
    </div>
  );
}
